import React, { useEffect, useMemo, useState } from "react";
import AppShell from "@/components/app/AppShell";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Home, Car, Ship, ChevronDown, ChevronUp, Filter, MapPin,
  ShieldCheck, DollarSign, Percent, FileText, Sparkles, TrendingUp, AlertTriangle, Info,
} from "lucide-react";

const US_STATES = [
  "AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA",
  "KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ",
  "NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT",
  "VA","WA","WV","WI","WY","DC",
];

const FAMILY_META = {
  mortgage: { label: "Home Loans", icon: Home, accent: "from-emerald-500 to-teal-500", tint: "text-emerald-600" },
  auto:     { label: "Auto Loans", icon: Car, accent: "from-slate-800 to-slate-600", tint: "text-slate-800" },
  boat:     { label: "Boat / RV / Marine", icon: Ship, accent: "from-sky-500 to-blue-500", tint: "text-sky-600" },
};

const DIFFICULTY_TONE = {
  "Low": "bg-emerald-100 text-emerald-800",
  "Medium-Low": "bg-lime-100 text-lime-800",
  "Medium": "bg-amber-100 text-amber-800",
  "Medium-High": "bg-orange-100 text-orange-800",
  "High": "bg-rose-100 text-rose-800",
  "Very High": "bg-rose-200 text-rose-900",
};

const ODDS_TONE = {
  "Very High": "bg-emerald-500 text-white",
  "High": "bg-emerald-400 text-emerald-900",
  "Moderate": "bg-amber-400 text-amber-950",
  "Low — close, work on score": "bg-orange-400 text-orange-950",
  "Not eligible yet": "bg-slate-300 text-slate-700",
};

export default function LoanPrograms() {
  const [family, setFamily] = useState("mortgage");
  const [category, setCategory] = useState("");
  const [state, setState] = useState("");
  const [creditScore, setCreditScore] = useState("");
  const [dti, setDti] = useState("");
  const [downPct, setDownPct] = useState("");
  const [expanded, setExpanded] = useState(null);
  const [data, setData] = useState({ programs: [], disclaimer: "" });
  const [loading, setLoading] = useState(false);
  const [families, setFamilies] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/funding/catalog");
        setFamilies(data.loan_families || []);
      } catch {}
    })();
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const params = { family };
      if (category) params.category = category;
      if (state) params.state = state;
      if (creditScore) params.credit_score = Number(creditScore);
      if (dti) params.dti = Number(dti);
      if (downPct) params.down_payment_pct = Number(downPct);
      const { data } = await api.get("/funding/loan-programs", { params });
      setData(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load programs");
    } finally { setLoading(false); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [family, category, state, creditScore, dti, downPct]);

  const catFor = useMemo(() => families.find((f) => f.key === family)?.categories || [], [families, family]);

  return (
    <AppShell>
      <div className="cq-in max-w-6xl">
        <p className="text-xs uppercase tracking-[0.28em] font-bold text-amber-600">My Funding Center · Loan Programs</p>
        <h1 className="font-display text-4xl sm:text-5xl font-black text-slate-900 mt-1">
          Real programs. Real numbers.
        </h1>
        <p className="text-slate-600 mt-2 max-w-3xl">
          Every mortgage, auto, and marine program lenders actually offer — with the credit score, DTI, down payment,
          docs, and difficulty for each. Add your numbers and we'll show your approval odds for each program.
        </p>

        {/* Family tabs */}
        <div className="mt-6 flex gap-2 flex-wrap" data-testid="loan-family-tabs">
          {families.map((f) => {
            const meta = FAMILY_META[f.key];
            if (!meta) return null;
            const Icon = meta.icon;
            const active = family === f.key;
            return (
              <button
                key={f.key}
                onClick={() => { setFamily(f.key); setCategory(""); }}
                className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-black transition
                  ${active ? `bg-gradient-to-r ${meta.accent} text-white shadow-lg` : "bg-white border-2 border-slate-200 text-slate-700 hover:border-slate-400"}`}
                data-testid={`loan-family-${f.key}`}
              >
                <Icon size={16} /> {f.emoji} {meta.label}
                <span className={`text-[10px] font-black rounded-full px-1.5 py-0.5 ${active ? "bg-white/30" : "bg-slate-100 text-slate-600"}`}>
                  {f.count}
                </span>
              </button>
            );
          })}
        </div>

        {/* Filters */}
        <Card className="mt-4 p-4 rounded-2xl border-slate-100">
          <div className="flex items-center gap-2 mb-3">
            <Filter size={14} className="text-slate-500" />
            <p className="text-[10px] uppercase tracking-widest font-black text-slate-500">Your numbers</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
            <div>
              <label className="text-[10px] font-black uppercase text-slate-500 block mb-1">Category</label>
              <select value={category} onChange={(e) => setCategory(e.target.value)}
                      className="w-full h-9 rounded-lg border border-slate-200 px-2 text-sm bg-white"
                      data-testid="loan-category-select">
                <option value="">All</option>
                {catFor.map((c) => <option key={c} value={c}>{c.replace(/_/g, " ")}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] font-black uppercase text-slate-500 block mb-1"><MapPin size={9} className="inline" /> State</label>
              <select value={state} onChange={(e) => setState(e.target.value)}
                      className="w-full h-9 rounded-lg border border-slate-200 px-2 text-sm bg-white"
                      data-testid="loan-state-select">
                <option value="">Any</option>
                {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] font-black uppercase text-slate-500 block mb-1"><TrendingUp size={9} className="inline" /> Credit</label>
              <Input value={creditScore} onChange={(e) => setCreditScore(e.target.value.replace(/\D/g, ""))}
                     placeholder="680" className="h-9" data-testid="loan-credit-input" />
            </div>
            <div>
              <label className="text-[10px] font-black uppercase text-slate-500 block mb-1"><Percent size={9} className="inline" /> DTI %</label>
              <Input value={dti} onChange={(e) => setDti(e.target.value)}
                     placeholder="38" className="h-9" data-testid="loan-dti-input" />
            </div>
            <div>
              <label className="text-[10px] font-black uppercase text-slate-500 block mb-1"><DollarSign size={9} className="inline" /> Down %</label>
              <Input value={downPct} onChange={(e) => setDownPct(e.target.value)}
                     placeholder="10" className="h-9" data-testid="loan-down-input" />
            </div>
            <div className="flex items-end">
              <Button variant="outline" size="sm" onClick={() => { setCategory(""); setState(""); setCreditScore(""); setDti(""); setDownPct(""); }}
                      className="w-full" data-testid="loan-clear-filters">
                Clear
              </Button>
            </div>
          </div>
        </Card>

        {/* Program list */}
        <div className="mt-4 space-y-3" data-testid="loan-programs-list">
          {loading && <p className="text-sm text-slate-400 py-6 text-center">Loading programs…</p>}
          {!loading && data.programs.length === 0 && (
            <Card className="p-8 text-center text-sm text-slate-500 rounded-2xl border-slate-100">
              No programs match your filters. Try clearing them.
            </Card>
          )}
          {!loading && data.programs.map((p) => {
            const isOpen = expanded === p.key;
            const odds = p.approval_odds;
            return (
              <Card key={p.key} className="p-0 rounded-2xl border-slate-100 overflow-hidden" data-testid={`loan-program-${p.key}`}>
                <button
                  onClick={() => setExpanded(isOpen ? null : p.key)}
                  className="w-full text-left p-4 sm:p-5 hover:bg-slate-50 transition"
                >
                  <div className="flex items-start gap-3 flex-wrap">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`text-[10px] uppercase tracking-widest font-black rounded-full px-2 py-0.5 ${DIFFICULTY_TONE[p.difficulty] || "bg-slate-100 text-slate-700"}`}>
                          {p.difficulty}
                        </span>
                        <span className="text-[10px] uppercase tracking-widest font-black text-slate-400">
                          {p.category?.replace(/_/g, " ")}
                        </span>
                        {p.state_restrictions?.length > 0 && (
                          <span className="text-[10px] uppercase tracking-widest font-black text-slate-500 bg-slate-100 rounded-full px-2 py-0.5">
                            <MapPin size={9} className="inline mr-0.5" /> {p.state_restrictions.length} states
                          </span>
                        )}
                      </div>
                      <h3 className="font-display text-lg sm:text-xl font-black text-slate-900 mt-1">{p.label}</h3>
                      <p className="text-sm text-slate-600 mt-1 line-clamp-2">{p.why}</p>
                      <div className="mt-2 flex flex-wrap gap-3 text-[11px] font-bold text-slate-700">
                        <span><TrendingUp size={11} className="inline text-emerald-600" /> {p.min_credit_score}+ score</span>
                        {p.min_dti != null && <span><Percent size={11} className="inline text-amber-600" /> ≤{p.min_dti}% DTI</span>}
                        <span><DollarSign size={11} className="inline text-slate-500" /> {p.min_down_payment_pct}% down</span>
                        {p.reserves_required_months > 0 && <span><ShieldCheck size={11} className="inline text-slate-500" /> {p.reserves_required_months}mo reserves</span>}
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-1 shrink-0">
                      {odds && (
                        <span className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-[11px] font-black uppercase tracking-widest ${ODDS_TONE[odds] || "bg-slate-200 text-slate-800"}`}>
                          <Sparkles size={10} /> {odds}
                        </span>
                      )}
                      {p.score_gap > 0 && (
                        <span className="text-[10px] font-bold text-rose-600">
                          Need +{p.score_gap} points
                        </span>
                      )}
                      {isOpen ? <ChevronUp size={16} className="text-slate-400 mt-2" /> : <ChevronDown size={16} className="text-slate-400 mt-2" />}
                    </div>
                  </div>
                </button>

                {/* Expanded details */}
                {isOpen && (
                  <div className="border-t border-slate-100 bg-slate-50/50 p-4 sm:p-5 space-y-4">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      <Stat label="Min score" value={`${p.min_credit_score}+`} />
                      <Stat label="Max DTI" value={p.min_dti != null ? `${p.min_dti}%` : "—"} />
                      <Stat label="Min down" value={`${p.min_down_payment_pct}%`} />
                      <Stat label="Reserves" value={p.reserves_required_months > 0 ? `${p.reserves_required_months} mo` : "None"} />
                    </div>
                    {p.max_loan_note && (
                      <div className="rounded-xl bg-white border border-slate-100 p-3">
                        <p className="text-[10px] uppercase tracking-widest font-black text-slate-500">Loan size guidance</p>
                        <p className="text-sm text-slate-800 font-bold mt-1">{p.max_loan_note}</p>
                      </div>
                    )}
                    {p.approval_odds_note && (
                      <div className="rounded-xl bg-emerald-50 border border-emerald-100 p-3">
                        <p className="text-[10px] uppercase tracking-widest font-black text-emerald-700 flex items-center gap-1">
                          <TrendingUp size={11} /> Approval odds rule of thumb
                        </p>
                        <p className="text-sm text-emerald-900 mt-1">{p.approval_odds_note}</p>
                      </div>
                    )}
                    <div>
                      <p className="text-[10px] uppercase tracking-widest font-black text-slate-500 mb-2 flex items-center gap-1">
                        <FileText size={11} /> Documents you'll need
                      </p>
                      <ul className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                        {(p.docs_required || []).map((d) => (
                          <li key={d} className="text-[13px] text-slate-700 flex items-start gap-2">
                            <span className="mt-1 h-1.5 w-1.5 rounded-full bg-slate-400 shrink-0" /> {d}
                          </li>
                        ))}
                      </ul>
                    </div>
                    {p.state_restrictions?.length > 0 && (
                      <div className="rounded-xl bg-white border border-slate-100 p-3">
                        <p className="text-[10px] uppercase tracking-widest font-black text-slate-500 flex items-center gap-1"><MapPin size={11} /> Available in</p>
                        <p className="text-[13px] text-slate-700 mt-1">{p.state_restrictions.join(" · ")}</p>
                      </div>
                    )}
                    {p.watch_out && (
                      <div className="rounded-xl bg-amber-50 border border-amber-200 p-3 flex gap-2">
                        <AlertTriangle size={14} className="text-amber-700 mt-0.5 shrink-0" />
                        <p className="text-[13px] text-amber-900"><b>Heads up:</b> {p.watch_out}</p>
                      </div>
                    )}
                  </div>
                )}
              </Card>
            );
          })}
        </div>

        {data.disclaimer && (
          <Card className="mt-6 p-4 rounded-2xl border-slate-100 bg-slate-50">
            <p className="text-[11px] text-slate-500 flex items-start gap-2">
              <Info size={12} className="mt-0.5 shrink-0" /> {data.disclaimer}
            </p>
          </Card>
        )}
      </div>
    </AppShell>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-xl bg-white border border-slate-100 p-3">
      <p className="text-[10px] uppercase tracking-widest font-black text-slate-500">{label}</p>
      <p className="font-display text-lg font-black text-slate-900 mt-0.5">{value}</p>
    </div>
  );
}
